import { Button, Col, Container, Form, Row } from "react-bootstrap"
import { navigateTo } from "../../redux/router/routerActions"
import { useAppDispatch } from "../../redux/store"
import QuickForm from "../../Components/Forms/QuickForm/QuickForm"
import { convertActiveQueryToUrl } from "../../Helpers/Query/queryHelpers";
import useActiveQueryHook from "../../Hooks/UseActiveQuery/useActiveQueryHook";
import { useBulkEditTurnoversMutation, useGetTurnoversQuery } from "../../services/bClientApi"
import MongoPagination from "../../Components/Pagination/MongoPagination"
import { TurnoverType } from "../../Types/turnoverTypes"
import TurnoverEntry from "../../Components/Turnover/TurnoverEntry/TurnoverEntry"
import { useState } from "react"
import { ConfirmModal } from "../../Components/Modals/confirmModal"
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook"
import useMyPropertiesSelectValues from "../../Hooks/UseMyProperties/UseMyPropertySelectValuesHook"
import useUserRefs from "../../Hooks/UseUsersQuery/UseUserRefs"
import useUsersSelectValues from "../../Hooks/UseUsersQuery/UseUsersSelectValues"

const statusOptions = [
    { value: 'new', text: 'New' },
    { value: 'pending', text: 'Pending' },
    { value: 'started', text: 'Started' },
    { value: 'completed', text: 'Completed' },
    { value: 'ready', text: 'Ready' },
]

export default function TurnoversPage() {

    const dispatch = useAppDispatch()

    const { myProperties } = useMyProperties()
    const propertyOptions = useMyPropertiesSelectValues(myProperties)
    const userRefs = useUserRefs()
    const userOptions = useUsersSelectValues(userRefs, { valueKey: '_id' })

    const [disableButtons, setDisableButtons] = useState(false)
    const [selectedTurnovers, setSelectedTurnovers] = useState<{ [key: string]: boolean }>({})
    const [bulkStatusValue, setBulkStatusValue] = useState('')
    const [activeConfirmationModal, setActiveConfirmationModal] = useState({
        active: false,
        title: '',
        message: '',
        onConfirm: () => { },
        onClose: () => { },
    })

    const resetConfirmationModal = () => {
        setActiveConfirmationModal({
            active: false,
            title: '',
            message: '',
            onConfirm: () => { },
            onClose: () => { },
        })
        setDisableButtons(false)
    }

    const [
        bulkEditTurnovers,
        {
            isLoading: isEditing,
        }
    ] = useBulkEditTurnoversMutation()

    const { activeQuery } = useActiveQueryHook({
        query: {},
        limit: 10,
    })

    const {
        data,
        error,
        isLoading,
    } = useGetTurnoversQuery({
        turnoversQuery: activeQuery.query,
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit,
        }
    }, {
        skip: activeConfirmationModal.active || isEditing || disableButtons
    })
    const turnoversList = data?.docs || []

    const updateSelectedStatuses = async () => {
        setDisableButtons(true)
        setActiveConfirmationModal(prev => ({
            ...prev,
            message: 'Updating statuses...',
        }))
        const updatedEntries = Object.keys(selectedTurnovers).map(id => ({
            _id: id,
            status: bulkStatusValue,
        }))
        await bulkEditTurnovers(updatedEntries).catch(err => {
            console.error('Error updating turnover statuses:', err)
        })
        setSelectedTurnovers({})
        setBulkStatusValue('')
        resetConfirmationModal()
    }

    const loadPage = (pageParams: { query: any, page: number, limit: number }) => {
        const destination = `/turnovers?${convertActiveQueryToUrl(pageParams)}`
        dispatch(navigateTo(destination))
    }

    return (
        <Container>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h1>Turnovers</h1>
                        <Button
                            variant="primary"
                            onClick={() => dispatch(navigateTo('/add-turnover'))}
                        >
                            Add New Turnover
                        </Button>
                    </div>
                    <hr />
                    {error && (<>{JSON.stringify(error)}<br /></>)}

                    <QuickForm
                        layout={{ xs: 2 }}
                        initialData={{
                            scheduledForStart: activeQuery.query.scheduledForStart,
                            scheduledForEnd: activeQuery.query.scheduledForEnd,
                            type: activeQuery.query.type,
                            status: activeQuery.query.status ? activeQuery.query.status.map((s: string) => ({ tag: s })) : [],
                            tempStatus: '',
                            propertyId: activeQuery.query.propertyId,
                            bookingId: activeQuery.query.bookingId,
                            assignedTo: activeQuery.query.assignedTo,
                        }}
                        fields={[
                            {
                                fieldName: 'scheduledForStart',
                                fieldLabel: 'Scheduled From',
                                fieldType: 'date',
                                placeholder: 'Scheduled From',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'scheduledForEnd',
                                fieldLabel: 'Scheduled To',
                                fieldType: 'date',
                                placeholder: 'Scheduled To',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'type',
                                fieldLabel: 'Type',
                                fieldType: 'text',
                                placeholder: 'Type',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'propertyId',
                                fieldLabel: 'Property',
                                fieldType: 'select',
                                placeholder: '- Select Property -',
                                options: propertyOptions,
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'bookingId',
                                fieldLabel: 'Booking ID',
                                fieldType: 'text',
                                placeholder: 'Booking ID',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'assignedTo',
                                fieldLabel: 'Assigned To',
                                fieldType: 'select',
                                placeholder: '- Select User -',
                                options: userOptions,
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'status',
                                fieldLabel: 'Status',
                                fieldType: 'quickTag',
                                placeholder: 'Status',
                                extra: {
                                    tempFieldName: 'tempStatus',
                                    tagEntryName: 'tag',
                                    buttons: statusOptions.map(opt => ({ tag: opt.value, text: opt.text })),
                                    options: {
                                        hideInput: true,
                                        hideBadges: true,
                                    },
                                },
                                layout: {
                                    xs: 12,
                                }
                            },
                        ]}
                        disabled={isLoading}
                        onSubmit={(values: any) => {
                            const query: any = { ...values }
                            if (values.status && values.status.length > 0) {
                                query.status = values.status.map((s: any) => s.tag)
                            } else {
                                delete query.status
                            }
                            delete query.tempStatus
                            loadPage({
                                query,
                                page: 1,
                                limit: activeQuery.limit
                            })
                        }}
                    />

                    {turnoversList && turnoversList.length > 0 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            className="me-2 mb-2"
                            onClick={() => {
                                if (Object.keys(selectedTurnovers).length === turnoversList.length) {
                                    setSelectedTurnovers({})
                                } else {
                                    const newSelected: { [key: string]: boolean } = {}
                                    turnoversList.forEach((turnover: TurnoverType) => {
                                        if (turnover._id) {
                                            newSelected[turnover._id] = true
                                        }
                                    })
                                    setSelectedTurnovers(newSelected)
                                }
                            }}
                        >
                            {Object.keys(selectedTurnovers).length === turnoversList.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    )}

                    {selectedTurnovers && Object.keys(selectedTurnovers).length > 0 && (
                        <div className="d-flex align-items-center mb-2">
                            <Form.Select
                                size="sm"
                                className="me-2"
                                style={{ width: 'auto' }}
                                value={bulkStatusValue}
                                onChange={(e) => setBulkStatusValue(e.target.value)}
                            >
                                <option value="">Select Status</option>
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                                ))}
                            </Form.Select>
                            <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                disabled={!bulkStatusValue}
                                onClick={() => {
                                    setActiveConfirmationModal({
                                        active: true,
                                        title: 'Update Status',
                                        message: `Are you sure you want to update ${Object.keys(selectedTurnovers).length} turnover(s) to "${bulkStatusValue}"?`,
                                        onConfirm: updateSelectedStatuses,
                                        onClose: () => {
                                            resetConfirmationModal()
                                        },
                                    })
                                }}
                            >
                                Update Status
                            </Button>
                        </div>
                    )}

                    <div className="turnovers-list-container">
                        {turnoversList && turnoversList.length > 0 ? (
                            <div>
                                {turnoversList.map((turnover: TurnoverType, idx: number) => {
                                    const isSelected = turnover._id ? !!selectedTurnovers[turnover._id] : false
                                    return (
                                        <TurnoverEntry
                                            key={`${turnover._id}-${idx}`}
                                            turnoverData={turnover}
                                            customButtons={[
                                                {
                                                    text: isSelected ? 'Deselect' : 'Select',
                                                    variant: isSelected ? 'danger' : 'primary',
                                                    onClick: (turnoverData: TurnoverType) => {
                                                        if (!turnoverData._id) return
                                                        if (selectedTurnovers[turnoverData._id]) {
                                                            const newSelected = { ...selectedTurnovers }
                                                            delete newSelected[turnoverData._id]
                                                            setSelectedTurnovers(newSelected)
                                                        } else {
                                                            setSelectedTurnovers({
                                                                ...selectedTurnovers,
                                                                [turnoverData._id]: true,
                                                            })
                                                        }
                                                    }
                                                }
                                            ]}
                                        />
                                    )
                                })}
                            </div>
                        ) : (
                            <div className="text-center p-4">
                                <h5>No turnovers found</h5>
                                <p>Try adjusting your search filters or add a new turnover.</p>
                            </div>
                        )}
                    </div>

                    <MongoPagination
                        data={data}
                        loadPage={(pageLoadInfo: { page: number, limit?: number }) => {
                            loadPage({
                                query: activeQuery.query,
                                limit: activeQuery.limit,
                                ...pageLoadInfo
                            })
                        }}
                    />
                </Col>
            </Row>

            <ConfirmModal
                active={activeConfirmationModal.active}
                title={activeConfirmationModal.title}
                message={activeConfirmationModal.message}
                onConfirm={() => {
                    activeConfirmationModal.onConfirm()
                }}
                onClose={() => {
                    activeConfirmationModal.onClose()
                }}
                disabled={isEditing || disableButtons}
            />
        </Container>
    )
}
